// ═══════════════════════════════════════════════════════════
// REGURE DESIGN SYSTEM — Shared constants, icons, and micro-components
// ═══════════════════════════════════════════════════════════
import React from 'react';
import i18nInstance from '../i18n';

// ─── Color / Style Constants ─────────────────────────────
export const S = {
  burgundy: '#800020',
  burgundy2: '#722f37',
  // Tenant-brandable tokens. These resolve to the Regure palette until a
  // tenant sets a colour, and are read by a deliberately short list of
  // surfaces — active navigation and the primary action button. Using them
  // anywhere else widens tenant branding beyond what was agreed; see
  // utils/brandTokens.js.
  brandPrimary: 'var(--regure-brand-primary, #800020)',
  brandAccent:  'var(--regure-brand-accent, #722f37)',
  burgundyLight: '#8b1538',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textMuted: '#64748b',
  borderLight: '#e2e8f0',
  bgCard: '#f8fafc',
  bgHover: '#f1f5f9',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
};

// ─── SVG Icon Library ────────────────────────────────────
export const Icons = {
  grid:          (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
  'file-text':   (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  'git-branch':  (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/></svg>,
  shield:        (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  'file-check':  (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/></svg>,
  calculator:    (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="14" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="10" y2="18"/><line x1="14" y1="18" x2="16" y2="18"/></svg>,
  receipt:       (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/><path d="M8 10h8"/><path d="M8 14h4"/></svg>,
  percent:       (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>,
  folder:        (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
  'message-circle': (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  video:         (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>,
  'pen-tool':    (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>,
  lock:          (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  'check-circle': (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  globe:         (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  'user-check':  (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>,
  users:         (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  briefcase:     (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  building:      (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20"/><path d="M9 22V12h6v10"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M8 10h.01"/><path d="M16 10h.01"/></svg>,
  key:           (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>,
  link:          (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  settings:      (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  search:        (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  plus:          (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  bell:          (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  chevronRight:  (c='currentColor', s=14) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  chevronDown:   (c='currentColor', s=14) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  arrowLeft:     (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  download:      (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  filter:        (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  clock:         (c='currentColor', s=16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  upload:        (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  moreVertical:  (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>,
  x:             (c='currentColor', s=16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  shieldCheck:   (c='currentColor', s=16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>,
  'trending-up': (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  'trending-down': (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>,
  activity:      (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  calendar:      (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  user:          (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  mail:          (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  phone:         (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.35 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  'map-pin':     (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  'dollar-sign': (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  'bar-chart':   (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>,
  refresh:       (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  eye:           (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  edit:          (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:         (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  check:         (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  'alert-circle': (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  'arrow-up':    (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>,
  'arrow-down':  (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>,
  'log-out':     (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  'alert-triangle': (c='currentColor', s=18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
};

export const Icon = ({ name, color, size }) => {
  const fn = Icons[name];
  return fn ? fn(color || 'currentColor', size || 18) : null;
};

// ─── Status / Priority Configs ───────────────────────────
export const STATUS_CONFIG = {
  new:           { label: 'New',           bg: '#dbeafe', color: '#1d4ed8' },
  open:          { label: 'Open',          bg: '#dcfce7', color: '#15803d' },
  review:        { label: 'Under Review',  bg: '#fef3c7', color: '#92400e' },
  investigation: { label: 'Investigation', bg: '#e0e7ff', color: '#4338ca' },
  negotiation:   { label: 'Negotiation',   bg: '#fce7f3', color: '#9d174d' },
  approved:      { label: 'Approved',      bg: '#d1fae5', color: '#065f46' },
  denied:        { label: 'Denied',        bg: '#fee2e2', color: '#991b1b' },
  closed:        { label: 'Closed',        bg: '#f1f5f9', color: '#64748b' },
  // Policy statuses
  active:        { label: 'Active',        bg: '#dcfce7', color: '#15803d' },
  expired:       { label: 'Expired',       bg: '#f1f5f9', color: '#64748b' },
  cancelled:     { label: 'Cancelled',     bg: '#fee2e2', color: '#991b1b' },
  pending:       { label: 'Pending',       bg: '#fef3c7', color: '#92400e' },
  bound:         { label: 'Bound',         bg: '#dbeafe', color: '#1d4ed8' },
  // Invoice statuses
  draft:         { label: 'Draft',         bg: '#f1f5f9', color: '#64748b' },
  sent:          { label: 'Sent',          bg: '#dbeafe', color: '#1d4ed8' },
  paid:          { label: 'Paid',          bg: '#d1fae5', color: '#065f46' },
  overdue:       { label: 'Overdue',       bg: '#fee2e2', color: '#991b1b' },
  // Offer statuses
  accepted:      { label: 'Accepted',      bg: '#d1fae5', color: '#065f46' },
  declined:      { label: 'Declined',      bg: '#fee2e2', color: '#991b1b' },
};

export const PRIORITY_CONFIG = {
  critical: { label: 'Critical', color: '#dc2626' },
  high:     { label: 'High',     color: '#ea580c' },
  medium:   { label: 'Medium',   color: '#d97706' },
  low:      { label: 'Low',      color: '#16a34a' },
};

// ─── Reusable Status Badge ────────────────────────────────
// Translates via i18n on `common.status.<statusKey>` with cfg.label as the
// English fallback. Fixes the 2026-08-06 report of raw "Active" / "Sent" /
// etc. leaking through on FR + DE locales in broker table, carrier table,
// invoice table, policy list, and broker/carrier detail headers.
// Uses the module-scope i18nInstance instead of the useTranslation hook
// so it works in non-React callsites too (some legacy screens render
// StatusBadge from render helpers rather than JSX components).
export const StatusBadge = ({ status, label }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  // Allow callers to override the label text without touching the shared
  // colour/style map — e.g. offers render `sent` as "Issued" because no
  // outbound send actually happens, while invoices keep "Sent".
  const text = label ?? i18nInstance.t(`common.status.${status}`, { defaultValue: cfg.label });
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 12px', borderRadius: 20,
      fontSize: 12, fontWeight: 600,
      background: cfg.bg, color: cfg.color,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color }} />
      {text}
    </span>
  );
};

// ─── Priority Dot ─────────────────────────────────────────
// Translates on `common.priority.<key>` with cfg.label as the English
// fallback — the same arrangement as StatusBadge above. This one rendered
// cfg.label directly, so "Critical" / "High" reached a French user; the two
// PriorityDot copies under ClaimsScreen were already translated.
export const PriorityDot = ({ priority }) => {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  const text = i18nInstance.t(`common.priority.${priority}`, { defaultValue: cfg.label });
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: S.textSecondary }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color }} />
      {text}
    </span>
  );
};

// ─── Avatar with Initials ──────────────────────────────────
export const Avatar = ({ initials, size = 32, radius = 8 }) => (
  <div style={{
    width: size, height: size, borderRadius: radius,
    background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontSize: size * 0.35, fontWeight: 700, flexShrink: 0,
  }}>
    {initials}
  </div>
);

// ─── Page Header ─────────────────────────────────────────
export const PageHeader = ({ breadcrumb, title, actions }) => (
  <header style={{
    padding: '20px 32px',
    borderBottom: `1px solid ${S.borderLight}`,
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(20px)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  }}>
    <div>
      {breadcrumb && (
        <div style={{ fontSize: 12, color: S.textMuted, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          {breadcrumb}
        </div>
      )}
      <div style={{ fontSize: 22, fontWeight: 700, color: S.textPrimary }}>{title}</div>
    </div>
    {actions && <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>{actions}</div>}
  </header>
);

// ─── Primary Button ───────────────────────────────────────
export const BtnPrimary = ({ children, onClick, style, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '10px 20px',
      background: disabled ? '#94a3b8' : `linear-gradient(135deg, ${S.brandPrimary}, ${S.brandAccent})`,
      color: '#fff', border: 'none', borderRadius: 10,
      fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
      ...style,
    }}
  >
    {children}
  </button>
);

// ─── Secondary Button ─────────────────────────────────────
export const BtnSecondary = ({ children, onClick, style }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '10px 16px',
      background: S.bgCard, border: `1px solid ${S.borderLight}`,
      borderRadius: 10, fontSize: 13, fontWeight: 500,
      color: S.textSecondary, cursor: 'pointer',
      ...style,
    }}
  >
    {children}
  </button>
);

// ─── Data Card ────────────────────────────────────────────
export const DataCard = ({ children, style }) => (
  <div style={{
    background: S.bgCard, border: `1px solid ${S.borderLight}`,
    borderRadius: 14, padding: 20, ...style,
  }}>
    {children}
  </div>
);

// ─── Table Helpers ────────────────────────────────────────
export const TH = ({ children, style }) => (
  <th style={{
    padding: '12px 14px', textAlign: 'left',
    fontSize: 11, fontWeight: 700, color: S.textMuted,
    textTransform: 'uppercase', letterSpacing: 0.8,
    borderBottom: `2px solid ${S.borderLight}`,
    ...style,
  }}>
    {children}
  </th>
);

export const TD = ({ children, style }) => (
  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary, ...style }}>
    {children}
  </td>
);

// ─── Empty State ─────────────────────────────────────────
export const EmptyState = ({ icon = 'shield', title, description, action }) => (
  <div style={{ textAlign: 'center', padding: '80px 20px' }}>
    <div style={{
      width: 64, height: 64, borderRadius: 16,
      background: S.bgCard, border: `2px dashed ${S.borderLight}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 16px',
    }}>
      <Icon name={icon} color="#94a3b8" size={28} />
    </div>
    <div style={{ fontSize: 16, fontWeight: 600, color: S.textPrimary, marginBottom: 6 }}>{title}</div>
    {description && <div style={{ fontSize: 13, color: S.textMuted, marginBottom: action ? 20 : 0 }}>{description}</div>}
    {action}
  </div>
);

// ─── Label/Value pair ─────────────────────────────────────
export const LabelValue = ({ label, value, valueStyle }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary, ...valueStyle }}>{value}</div>
  </div>
);

// ─── Formatters ───────────────────────────────────────────
// ─── Locale Config (admin-configurable) ──────────────────
// Default: CH / CHF / MM/DD/YYYY. Dynamically reads from window.REGURE_CURRENCY set by user preferences.
export const LOCALE_CONFIG = {
  region:       typeof window !== 'undefined' && window.REGURE_REGION   || 'CH',
  currency:     typeof window !== 'undefined' && window.REGURE_CURRENCY || 'CHF',
  currencyCode: typeof window !== 'undefined' && window.REGURE_CURRENCY || 'CHF',
  dateFormat:   typeof window !== 'undefined' && window.REGURE_DATE_FMT || 'MM/DD/YYYY',
};

// `decimals` is ISO 4217 minor units, and it is only stated where it is NOT
// the assumed two. The Bahraini dinar divides into 1000 fils, so a BHD
// figure is written to three places — a Bahraini insurer's book records
// 1,285.500, and "BHD 1,285.5" reads to an underwriter there the way
// "$1,285.5" reads here. Mirrored by CURRENCY_DECIMALS in
// backend/services/genericOfferDocument.js so a figure on screen and the
// same figure on the proposal cannot disagree.
const CURRENCY_LOCALES = {
  USD: { locale: 'en-US', symbol: '$' },
  CHF: { locale: 'de-CH', symbol: 'CHF' },
  EUR: { locale: 'de-DE', symbol: '€' },
  GBP: { locale: 'en-GB', symbol: '£' },
  AED: { locale: 'ar-AE', symbol: 'AED' },
  BHD: { locale: 'en-BH', symbol: 'BHD', decimals: 3 },
  KWD: { locale: 'en-KW', symbol: 'KWD', decimals: 3 },
  OMR: { locale: 'en-OM', symbol: 'OMR', decimals: 3 },
  QAR: { locale: 'en-QA', symbol: 'QAR' },
  SAR: { locale: 'en-SA', symbol: 'SAR' },
};

const DATE_LOCALES = {
  'MM/DD/YYYY': 'en-US',
  'DD/MM/YYYY': 'en-GB',
  'DD.MM.YYYY': 'de-CH',
  'YYYY-MM-DD': 'sv-SE',
};

// Root cause of the "16. Juli 2026 under French UI" bug (Test Run 7-22):
// formatDateLong picked the locale from window.REGURE_DATE_FMT ONLY, which
// carried the tenant's date-format preference (DD.MM.YYYY → de-CH) with no
// awareness of the active UI language. ARGOS's Swiss tenant has DD.MM.YYYY
// but Yann uses French, so month names rendered in German.
// Fix: for long-form dates (which include a month NAME), the UI language
// wins. dateFmt still controls numeric ordering in formatDate. If the UI
// language has no explicit region variant, fall back to a sensible regional
// default (fr → fr-CH for Swiss/EU brokers, de → de-CH, en → en-US).
const LANG_TO_LOCALE = {
  en: 'en-US',
  fr: 'fr-CH',
  de: 'de-CH',
};
const getActiveLang = () => (i18nInstance?.language?.split('-')[0]) || 'en';
const localeForLanguage = () => LANG_TO_LOCALE[getActiveLang()] || 'en-US';

// Read window globals live so changes in Account Settings take effect
// on the next render without a page reload.
// Resolve which currency a figure is denominated in.
//
//   1. the currency explicitly persisted on the record / transaction
//   2. the tenant's configured currency
//   3. nothing — render the bare number
//
// There is deliberately NO 'CHF' fallback. The portal screens hardcoded CHF
// because the product started in Switzerland, so a Spanish tenant's EUR
// premiums rendered as Swiss francs. A wrong currency symbol on a money
// figure is worse than no symbol at all, so the last resort is bare digits.
export const resolveCurrency = (explicit) => {
  const fromRecord = String(explicit || '').trim().toUpperCase();
  if (fromRecord) return fromRecord;
  const tenant = typeof window !== 'undefined' && window.REGURE_CURRENCY;
  return tenant ? String(tenant).toUpperCase() : null;
};

/**
 * Format a monetary figure.
 *
 * @param amount    the number
 * @param explicit  the currency persisted ON THE RECORD, when it has one.
 *                  Pass it whenever the record carries `currency` — an
 *                  explicitly USD-denominated policy must not be relabelled
 *                  as the tenant's currency. This formats only; it never
 *                  converts.
 */
// A currency's minor units from the platform's own ISO 4217 data (JPY 0,
// CHF 2, BHD 3); 2 when the currency is unknown or absent.
const minorUnits = (currency) => {
  if (!currency) return 2;
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
  } catch {
    return 2;
  }
};

export const formatMoney = (amount, explicit) => {
  const currency = resolveCurrency(explicit);
  const cfg = (currency && CURRENCY_LOCALES[currency]) || null;
  const locale = cfg?.locale
    || (typeof window !== 'undefined' && window.REGURE_NUMBER_LOCALE)
    || undefined;
  // A money figure always shows its currency's full minor units: CHF 305.50,
  // never 305.5, and CHF 1'044.00, never 1'044. The minimum used to be 0,
  // which trimmed trailing zeros on every screen (QA 2026-09-28: the PGM
  // form's "CHF 305.5"). Hundredths unless the currency says otherwise — a
  // currency written in thousandths must not be rounded to hundredths on
  // the way to the screen: 1285.500 BHD is not 1285.5.
  const dp = cfg?.decimals ?? minorUnits(currency);
  const formatted = Number(amount || 0).toLocaleString(locale, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
  if (!currency) return formatted;
  if (currency === 'USD') return `$${formatted}`;
  return `${cfg?.symbol || currency} ${formatted}`;
};

// Pass the record's own currency through. This used to drop its second
// argument, so every screen calling formatCurrency(policy.annualPremium)
// silently fell back to the caller's resolved currency — the internal Policy
// Register rendered `$380` for a policy whose record says `currency: "EUR"`,
// while the portal (which called formatMoney directly) rendered `€ 380` for
// the same row. Same data, two answers, because one helper forgot an argument.
export const formatCurrency = (amount, explicit) => formatMoney(amount, explicit);

// Legacy alias used throughout existing screens
export const formatCHF = formatCurrency;

// Numeric date: the tenant's dateFmt controls day/month ordering. Language
// doesn't matter for a pure numeric date, so DATE_LOCALES stays the source.
// The tenant's configured dateFormat is authoritative for every displayed
// date. Call sites must use this (or formatDateLong) rather than
// toLocaleDateString, which silently follows the BROWSER locale and is why
// one tenant saw 31/05/2026 in a table and 8/14/2026 two screens away.
//
// The platform default is only reached when no tenant format has resolved
// yet — during the pre-hydration window on a cold load.
// A calendar date with no time component. `2026-05-31` is the 31st of May in
// every country; it is not an instant and no timezone may move it.
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const dateFmt = (typeof window !== 'undefined' && window.REGURE_DATE_FMT) || 'MM/DD/YYYY';
  const locale = DATE_LOCALES[dateFmt] || 'en-US';

  // ── Date-only values pass through untouched ──────────────
  //
  // `new Date('2026-05-31')` parses as midnight UTC, and rendering that in a
  // negative-offset zone lands on the 30th. A date-only field has no instant
  // to convert, so the calendar parts are rendered exactly as given — pinned
  // to UTC purely so the formatter cannot re-interpret them.
  const raw = typeof dateStr === 'string' ? dateStr.trim() : '';
  const parts = raw.match(DATE_ONLY_RE);
  if (parts) {
    const [, y, m, d] = parts;
    return new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)))
      .toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
  }

  // ── Instants render in the resolved zone ─────────────────
  //
  // This used to omit `timeZone` entirely, so a stored instant rendered in
  // whatever zone the VIEWER's machine happened to be in: CL-2026-06981,
  // reported 2026-05-31T00:14Z, read "31/05/2026" from Madrid and
  // "30/05/2026" from Denver. Same record, same tenant, two calendar days.
  //
  // window.REGURE_TIMEZONE is resolved in App.js as user preference → tenant
  // config, and is left unset when neither supplied one — so the viewer's own
  // clock is still the fallback, rather than a zone the tenant does not use.
  const tz = (typeof window !== 'undefined' && window.REGURE_TIMEZONE) || undefined;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    ...(tz ? { timeZone: tz } : {}),
  });
};

// Date + time, same tenant ordering. Workflow rules and audit rows want the
// clock as well; they were calling toLocaleString() directly and rendering
// US order on a DD/MM tenant.
// Timestamps are stored in UTC and must be DISPLAYED in the tenant's zone
// (see the timezone policy). Without an explicit `timeZone` this rendered in
// whatever zone the viewer's machine happened to be in, so the same audit
// row read differently for a colleague one country over.
export const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const dateFmt = (typeof window !== 'undefined' && window.REGURE_DATE_FMT) || 'MM/DD/YYYY';
  const locale = DATE_LOCALES[dateFmt] || 'en-US';
  const tz = (typeof window !== 'undefined' && window.REGURE_TIMEZONE) || undefined;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    // Omitted rather than defaulted to 'UTC': before the tenant zone
    // resolves, the viewer's own clock is a better guess than stamping a
    // zone the tenant does not use.
    ...(tz ? { timeZone: tz } : {}),
  });
};

// Long-form date: month name is language-dependent. Use active i18n language.
//
// Reserve this for PROSE — a date inside a sentence, where a month name
// reads better and must be in the reader's language. Every other date is a
// field value (a table cell, a labelled figure, a form echo) and belongs to
// formatDate, which follows the TENANT's configured ordering. Mixing the
// two on one screen is what made Case Management show "Aug 13, 2026" one
// column away from "31/05/2026" on a DD/MM/YYYY tenant.
export const formatDateLong = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(localeForLanguage(), { day: 'numeric', month: 'short', year: 'numeric' });
};

// Number formatter respecting the active language (thousands separators,
// decimal marks). Use in place of .toLocaleString() without a locale arg,
// which silently uses the browser default and diverges by machine.
export const formatNumber = (n, opts = { minimumFractionDigits: 0, maximumFractionDigits: 2 }) => {
  if (n == null || n === '') return '—';
  return Number(n).toLocaleString(localeForLanguage(), opts);
};
